import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useTheme, alpha } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import CustomTextField from '../../_common/_form_fields/CustomTextField'
import CustomSelectField from '../../_common/_form_fields/CustomSelectField'
import CustomCheckboxField from '../../_common/_form_fields/CustomCheckboxField'
import CustomDialog from '../../_common/CustomDialog'
import CustomBackdrop from '../../_common/CustomBackdrop'
import defaultTexts from '../../../texts/texts'
import constants from '../../../config/constants'
import { request } from '../../../_network/request'
import { serviceConfigsURLs, billingAccountServicesURLs } from '../../../_network/apiUrls'
import { checkAccessRequest } from '../../../_network/request'
import { capitalizeFirstLetter } from '../../_common/_helpers/string'
import { servicesSuspendPolicy, servicesFinalPolicy } from '../_common/billingConstants'
import { isBillingAvailable, getAllowedRoles } from '../_helpers/billing'

const ServiceAdd = (props) => {
  const {
    subscriptionName,
    selectedService,
    services,
    supportedServiceConfigs,
    showBillingService,
    setFetchServices,
    handleServiceAddDialogClose
  } = props
  const theme = useTheme()

  const account_id = useSelector(state => state.profile.account_id)
  
  const [serviceDomain, setServiceDomain] = useState('')
  const [apiVersion, setApiVersion] = useState('')
  const [isIdentityProvider, setIsIdentityProvider] = useState(false)
  const [defaultRegion, setDefaultRegion] = useState('')
  const [regionName, setRegionName] = useState('')
  const [appCredentialId, setAppCredentialId] = useState('')
  const [appCredentialSecret, setAppCredentialSecret] = useState('')
  const [visibleToCustomer, setVisibleToCustomer] = useState(true)

  const [enableBilling, setEnableBilling] = useState(false)
  const [suspendAction, setSuspendAction] = useState('')
  const [suspendDelay, setSuspendDelay] = useState('')
  const [finalAction, setFinalAction] = useState('')
  const [finalActionDelay, setFinalActionDelay] = useState('')
  
  const [helperText, setHelperText] = useState('')
  const [helperDialogOpen, setHelperDialogOpen] = useState(false)
  const [identityProviderExists, setIdentityProviderExists] = useState(false)
  const [existedRegionNames, setExistedRegionNames] = useState([])
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  const hasBilling = isBillingAvailable(selectedService, supportedServiceConfigs)
  const isOnlyBackup = subscriptionName === 'openstackBackupAutomation'

  const formControlStyle = {
    background: theme.palette.aliceBlue,
    color: theme.palette.vodka,
    borderRadius: '5px',
    width: '100%',
  }

  const outlinedInputStyle = {
    '& .MuiInputBase-input::label': {
      color: theme.palette.vodka,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.vodka,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.vodka,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.vodka,
    },
  }

  const fieldBoxStyle = {
    padding: '12px 0px'
  }

  const helpIconStyle = {
    color: theme.palette.oxfordBlue,
    marginTop: '-5px',
  }

  const checkboxStyle = {
    color: theme.palette.violetsAreBlue,

    '&.Mui-checked': {
      color: theme.palette.violetsAreBlue,
    },

    '& .MuiSvgIcon-root': {
      fontSize: 24,
    },
  }

  const checkboxLabelStyle = {
    color: theme.palette.oxfordBlue,
    fontWeight: '500',
  }

  const errorHelperStyle = {
    color: theme.palette.coralRed,
    paddingLeft: '5px',
  }

  const saveButtonStyle = {
    fontWeight: '500',
    color: 'white',
    background: theme.palette.greenSheen,
    fontSize: '18px',
    height: '40px',
    borderRadius: '20px',
    padding: '0px 30px',
    marginTop: '20px',
    textTransform: 'capitalize',

    '&:hover': {
      background: alpha(theme.palette.greenSheen, 0.8),
    },
  }

  const getSelectFieldList = (service, field, sub_field) => {
    let result = []
    let options_list = []

    const service_object = supportedServiceConfigs.find(item => item.service === service)

    if (sub_field && service_object && service_object.config_params && service_object.config_params[field] && service_object.config_params[field][sub_field] && service_object.config_params[field][sub_field].length > 0) {
      options_list = service_object.config_params[field][sub_field]
    } else if (service_object && service_object.config_params && service_object.config_params[field] && service_object.config_params[field].length > 0) {
      options_list = service_object.config_params[field]
    }

    options_list.forEach(item => {
      result.push({
        keyword: capitalizeFirstLetter(item),
        value: item,
      })
    })

    return result
  }

  const handleHelperDialogOpen = (value) => {
    setHelperDialogOpen(true)
    setHelperText(value)
  }

  const handleHelperDialogClose = () => {
    setHelperDialogOpen(false)
    setHelperText('')
  }

  const handleChangeServiceDomain = (e) => {
    const value = e.target.value
    setServiceDomain(value)

    if (value) {
      setErrors(prev => { return { ...prev, serviceDomain: null } })
    }
  }

  const handleChangeApiVersion = (value) => {
    setApiVersion(value)

    if (value) {
      setErrors(prev => { return { ...prev, apiVersion: null } })
    }
  }

  const handleChangeIsIdentityProvider = (value) => {
    setIsIdentityProvider(value)
  }

  const handleChangeDefaultRegion = (e) => {
    const value = e.target.value
    setDefaultRegion(value)

    if (value) {
      setErrors(prev => { return { ...prev, defaultRegion: null } })
    }
  }

  const handleChangeRegionName = (e) => {
    const value = e.target.value
    setRegionName(value)

    if (value) {
      setErrors(prev => { return { ...prev, regionName: null } })
    }
  }

  const handleChangeAppCredentialId = (e) => {
    const value = e.target.value
    setAppCredentialId(value)

    if (value) {
      setErrors(prev => { return { ...prev, appCredentialId: null } })
    }
  }

  const handleChangeAppCredentialSecret = (e) => {
    const value = e.target.value
    setAppCredentialSecret(value)

    if (value) {
      setErrors(prev => { return { ...prev, appCredentialSecret: null } })
    }
  }

  const handleChangeVisibleToCustomer = (value) => {
    setVisibleToCustomer(value)
  }

  const handleChangeEnableBilling = (value) => {
    setEnableBilling(value)
  }

  const handleChangeSuspendAction = (value) => {
    setSuspendAction(value)
    
    if (value) {
      setErrors(prev => { return { ...prev, suspendAction: null } })
    }
  }

  const handleChangeSuspendDelay = (e) => {
    const value = e.target.value
    setSuspendDelay(value)
    
    if (value) {
      setErrors(prev => { return { ...prev, suspendDelay: null } })
    }
  }

  const handleChangeFinalAction = (value) => {
    setFinalAction(value)
    
    if (value) {
      setErrors(prev => { return { ...prev, finalAction: null } })
    }
  }

  const handleChangeFinalActionDelay = (e) => {
    const value = e.target.value
    setFinalActionDelay(value)
    
    if (value) {
      setErrors(prev => { return { ...prev, finalActionDelay: null } })
    }
  }

  const handleCheckAccess = async () => {
    const response = await checkAccessRequest(serviceDomain)

    if (response && response.status_code && response.status_code < 400) {
      return true
    }

    return false
  }

  const addRequest = async (backendPostData, billingPostData) => {
    const backend_url = constants.core_service_domain + '/' + serviceConfigsURLs.getClientServiceConfigs
    const backend_request_data = { url: backend_url, method: 'POST', data: backendPostData }
    await request(backend_request_data)

    if (billingPostData) {
      const billing_url = constants.billing_url + '/' + billingAccountServicesURLs.getAccountServices
      const billing_request_data = { url: billing_url, method: 'POST', data: billingPostData, headers: { 'customer-account-id': account_id } }
      await request(billing_request_data)
    }
    
    handleServiceAddDialogClose()
    setFetchServices(prev => prev + 1)
  }

  const handleSubmit = async () => {
    setIsLoading(true)
    let _errors = {}

    const access_checked = await handleCheckAccess()

    if (!serviceDomain) {
      _errors.serviceDomain = defaultTexts.pleaseFillOut
    } else if (!access_checked) {
      _errors.serviceDomain = defaultTexts.accessDenied
    }

    if (!apiVersion) {
      _errors.apiVersion = defaultTexts.pleaseFillOut
    }

    if (isIdentityProvider && !defaultRegion) {
      _errors.defaultRegion = defaultTexts.pleaseFillOut
    } else if (isIdentityProvider && defaultRegion && existedRegionNames.includes(defaultRegion)) {
      _errors.defaultRegion = defaultTexts.serviceInThisRegionAlreadyExists
    }

    if (!isIdentityProvider && !regionName) {
      _errors.regionName = defaultTexts.pleaseFillOut
    } else if (isIdentityProvider && regionName && existedRegionNames.includes(regionName)) {
      _errors.regionName = defaultTexts.serviceInThisRegionAlreadyExists
    }

    if (isIdentityProvider && !appCredentialId && !isOnlyBackup) {
      _errors.appCredentialId = defaultTexts.pleaseFillOut
    }

    if (isIdentityProvider && !appCredentialSecret && !isOnlyBackup) {
      _errors.appCredentialSecret = defaultTexts.pleaseFillOut
    }

    if (enableBilling && !suspendAction) {
      _errors.suspendAction = defaultTexts.pleaseFillOut
    }

    if (enableBilling && !suspendDelay) {
      _errors.suspendDelay = defaultTexts.pleaseFillOut
    }

    if (enableBilling && !finalAction) {
      _errors.finalAction = defaultTexts.pleaseFillOut
    }

    if (enableBilling && !finalActionDelay) {
      _errors.finalActionDelay = defaultTexts.pleaseFillOut
    }

    if (Object.keys(_errors).length === 0) {
      const service_object = services.find(item => item.service === selectedService)

      let backendPostData = {
        service_group: service_object.service_group,
        service_type: 'backend',
        service: selectedService,
        title: service_object.title,
        config_params: {
          service_domain: serviceDomain,
          api_version: apiVersion,
          is_identity_provider: isIdentityProvider,
          allowed_roles: getAllowedRoles(selectedService, supportedServiceConfigs),
          visible_to_service: visibleToCustomer ? ['custom_admin_ui', 'custom_client_ui'] : ['custom_admin_ui'],
        },
      }

      if (isIdentityProvider) {
        backendPostData.config_params.default_region = defaultRegion
        backendPostData.app_credential_id = appCredentialId
        backendPostData.app_credential_secret = appCredentialSecret
      } else {
        backendPostData.config_params.region_name = regionName
      }

      let billingPostData = null

      if (enableBilling) {
        billingPostData = {
          account_id: account_id,
          is_identity_provider: isIdentityProvider,
          service_name: selectedService,
          service_domain: serviceDomain,
          service_version: apiVersion,
          region_name: regionName ? regionName : defaultRegion,
          resource_suspend_period: parseInt(suspendDelay),
          resource_delete_period: parseInt(finalActionDelay),
          resource_suspend_policy: suspendAction,
          resource_delete_policy: finalAction,
        }

        if (isIdentityProvider) {
          billingPostData.credentials = {
            app_auth_id: appCredentialId,
            app_auth_secret: appCredentialSecret,
          }
        }
      }      

      addRequest(backendPostData, billingPostData)
    } else {
      setErrors(_errors)
    }

    setIsLoading(false)
  }
  
  useEffect(() => {
    const getExistedRegionNames = () => {
      let result = []

      services.forEach(item => {
        const region_name = item.config_params.is_identity_provider ? item.config_params.default_region : item.config_params.region_name
        if (!result.includes(region_name)) {
          result.push(region_name)
        }
      })

      return result
    }

    const service_object = services.find(item => item.config_params.is_identity_provider === true)

    setIdentityProviderExists(service_object ? true : false)
    setExistedRegionNames(getExistedRegionNames())
  }, [services])

  return (
    <>
      { isLoading && <CustomBackdrop open={isLoading} /> }

      <Box sx={fieldBoxStyle}>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='flex-start'
        >
          <Box sx={{ width: '60px' }}>
            <IconButton sx={helpIconStyle} onClick={() => handleHelperDialogOpen(defaultTexts.serviceDomainHelperText)}>
              <HelpOutlineIcon fontSize='large' />
            </IconButton>
          </Box>
          <Box sx={{ width: 'calc(90% - 60px)' }}>
            <CustomTextField 
              variant='outlined'
              label={defaultTexts.serviceDomain}
              currentValue={serviceDomain}
              setCurrentValue={handleChangeServiceDomain}
              required={true}
              error={errors.serviceDomain ? true : false}
              loginHelperText={errors.serviceDomain}
              loginHelperSx={errorHelperStyle}
              size='small'
              sx={formControlStyle}
              outlinedInputSx={outlinedInputStyle}
            />
          </Box>          
        </Stack>
      </Box>

      <Box sx={fieldBoxStyle}>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='flex-start'
        >
          <Box sx={{ width: '60px' }}></Box>
          <Box sx={{ width: 'calc(90% - 60px)' }}>
            <CustomSelectField
              label={defaultTexts.apiVersion}
              items={getSelectFieldList(selectedService, 'api_version')}
              currentValue={apiVersion}
              setCurrentValue={handleChangeApiVersion}
              required={true}
              error={errors.apiVersion ? true : false}
              loginHelperText={errors.apiVersion}
              loginHelperSx={errorHelperStyle}
              self_item_titles={true}
              size='small'
              sx={formControlStyle}
              outlinedInputSx={outlinedInputStyle}
            />
          </Box>          
        </Stack>
      </Box>

      {
        !identityProviderExists &&
        <Box sx={fieldBoxStyle}>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='flex-start'
          >
            <Box sx={{ width: '60px' }}>
              <IconButton sx={helpIconStyle} onClick={() => handleHelperDialogOpen(defaultTexts.isIdentityProviderHelperText)}>
                <HelpOutlineIcon fontSize='large' />
              </IconButton>
            </Box>
            <Box sx={{ width: 'calc(90% - 60px)' }}>
              <CustomCheckboxField
                label={defaultTexts.isIdentityProvider}
                checked={isIdentityProvider}
                handleChange={handleChangeIsIdentityProvider}
                disabled={identityProviderExists}
                sx={checkboxStyle}
                sxLabel={checkboxLabelStyle}
              />
            </Box>          
          </Stack>
        </Box>
      }

      <Box sx={fieldBoxStyle}>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='flex-start'
        >
          <Box sx={{ width: '60px' }}></Box>
          <Box sx={{ width: 'calc(90% - 60px)' }}>
            {
              isIdentityProvider ?
              <CustomTextField 
                variant='outlined'
                label={defaultTexts.defaultRegion}
                currentValue={defaultRegion}
                setCurrentValue={handleChangeDefaultRegion}
                required={true}
                error={errors.defaultRegion ? true : false}
                loginHelperText={errors.defaultRegion}
                loginHelperSx={errorHelperStyle}
                size='small'
                sx={formControlStyle}
                outlinedInputSx={outlinedInputStyle}
              /> :
              <CustomTextField 
                variant='outlined'
                label={defaultTexts.regionName}
                currentValue={regionName}
                setCurrentValue={handleChangeRegionName}
                required={true}
                error={errors.regionName ? true : false}
                loginHelperText={errors.regionName}
                loginHelperSx={errorHelperStyle}
                size='small'
                sx={formControlStyle}
                outlinedInputSx={outlinedInputStyle}
              />
            }
          </Box>          
        </Stack>
      </Box>

      {
        (isIdentityProvider && !isOnlyBackup) &&
        <>
          <Box sx={fieldBoxStyle}>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='flex-start'
            >
              <Box sx={{ width: '60px' }}>
                <IconButton sx={helpIconStyle} onClick={() => handleHelperDialogOpen(defaultTexts.serviceDomainHelperText)}>
                  <HelpOutlineIcon fontSize='large' />
                </IconButton>
              </Box>
              <Box sx={{ width: 'calc(90% - 60px)' }}>
                <CustomTextField 
                  variant='outlined'
                  label={defaultTexts.appCredentialId}
                  currentValue={appCredentialId}
                  setCurrentValue={handleChangeAppCredentialId}
                  required={true}
                  error={errors.appCredentialId ? true : false}
                  loginHelperText={errors.appCredentialId}
                  loginHelperSx={errorHelperStyle}
                  size='small'
                  sx={formControlStyle}
                  outlinedInputSx={outlinedInputStyle}
                />
              </Box>          
            </Stack>
          </Box>

          <Box sx={fieldBoxStyle}>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='flex-start'
            >
              <Box sx={{ width: '60px' }}>
                <IconButton sx={helpIconStyle} onClick={() => handleHelperDialogOpen(defaultTexts.serviceDomainHelperText)}>
                  <HelpOutlineIcon fontSize='large' />
                </IconButton>
              </Box>
              <Box sx={{ width: 'calc(90% - 60px)' }}>
                <CustomTextField 
                  variant='outlined'
                  label={defaultTexts.appCredentialSecret}
                  currentValue={appCredentialSecret}
                  setCurrentValue={handleChangeAppCredentialSecret}
                  required={true}
                  error={errors.appCredentialSecret ? true : false}
                  loginHelperText={errors.appCredentialSecret}
                  loginHelperSx={errorHelperStyle}
                  size='small'
                  sx={formControlStyle}
                  outlinedInputSx={outlinedInputStyle}
                />
              </Box>          
            </Stack>
          </Box>
        </>
      }

      {
        (!isIdentityProvider && !isOnlyBackup) &&
        <Box sx={fieldBoxStyle}>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='flex-start'
          >
            <Box sx={{ width: '60px' }}>
              <IconButton sx={helpIconStyle} onClick={() => handleHelperDialogOpen(defaultTexts.visibleToOpenStackCustomerUiHelperText)}>
                <HelpOutlineIcon fontSize='large' />
              </IconButton>
            </Box>
            <Box sx={{ width: 'calc(90% - 60px)' }}>
              <CustomCheckboxField
                label={defaultTexts.visibleToOpenStackCustomerUi}
                checked={visibleToCustomer}
                handleChange={handleChangeVisibleToCustomer}
                sx={checkboxStyle}
                sxLabel={checkboxLabelStyle}
              />
            </Box>          
          </Stack>
        </Box>
      }

      {
        (showBillingService && hasBilling) &&
        <>
          <Box sx={fieldBoxStyle}>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='flex-start'
            >
              <Box sx={{ width: '60px' }}></Box>
              <Box sx={{ width: 'calc(90% - 60px)' }}>
                <CustomCheckboxField
                  label={defaultTexts.enableBillingService}
                  checked={enableBilling}
                  handleChange={handleChangeEnableBilling}
                  sx={checkboxStyle}
                  sxLabel={checkboxLabelStyle}
                />
              </Box>          
            </Stack>
          </Box>

          {
            enableBilling &&
            <>
              <Box sx={fieldBoxStyle}>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='flex-start'
                >
                  <Box sx={{ width: '60px' }}></Box>
                  <Box sx={{ width: 'calc(90% - 60px)' }}>
                    <CustomSelectField
                      label={defaultTexts.suspendAction}
                      items={selectedService ? servicesSuspendPolicy[selectedService] : []} 
                      currentValue={suspendAction}
                      setCurrentValue={handleChangeSuspendAction}
                      required={true}
                      error={errors.suspendAction ? true : false}
                      loginHelperText={errors.suspendAction}
                      loginHelperSx={errorHelperStyle}
                      item_titles={defaultTexts}
                      size='small'
                      sx={formControlStyle}
                      outlinedInputSx={outlinedInputStyle}
                    />
                  </Box>          
                </Stack>
              </Box>

              <Box sx={fieldBoxStyle}>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='flex-start'
                >
                  <Box sx={{ width: '60px' }}></Box>
                  <Box sx={{ width: 'calc(90% - 60px)' }}>
                    <CustomTextField
                      type='number'
                      min={1}
                      variant='outlined'
                      label={defaultTexts.suspendDelayMinutes}
                      currentValue={suspendDelay}
                      setCurrentValue={handleChangeSuspendDelay}
                      required={true}
                      error={errors.suspendDelay ? true : false}
                      loginHelperText={errors.suspendDelay}
                      loginHelperSx={errorHelperStyle}
                      size='small'
                      sx={formControlStyle}
                      outlinedInputSx={outlinedInputStyle}
                    />
                  </Box>          
                </Stack>
              </Box>

              <Box sx={fieldBoxStyle}>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='flex-start'
                >
                  <Box sx={{ width: '60px' }}></Box>
                  <Box sx={{ width: 'calc(90% - 60px)' }}>
                    <CustomSelectField
                      label={defaultTexts.finalAction}
                      items={selectedService ? servicesFinalPolicy[selectedService] : []} 
                      currentValue={finalAction}
                      setCurrentValue={handleChangeFinalAction}
                      required={true}
                      error={errors.finalAction ? true : false}
                      loginHelperText={errors.finalAction}
                      loginHelperSx={errorHelperStyle}
                      item_titles={defaultTexts}
                      size='small'
                      sx={formControlStyle}
                      outlinedInputSx={outlinedInputStyle}
                    />
                  </Box>          
                </Stack>
              </Box>

              <Box sx={fieldBoxStyle}>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='flex-start'
                >
                  <Box sx={{ width: '60px' }}></Box>
                  <Box sx={{ width: 'calc(90% - 60px)' }}>
                    <CustomTextField
                      type='number'
                      min={1}
                      variant='outlined'
                      label={defaultTexts.finalActionDelayMinutes}
                      currentValue={finalActionDelay}
                      setCurrentValue={handleChangeFinalActionDelay}
                      required={true}
                      error={errors.finalActionDelay ? true : false}
                      loginHelperText={errors.finalActionDelay}
                      loginHelperSx={errorHelperStyle}
                      size='small'
                      sx={formControlStyle}
                      outlinedInputSx={outlinedInputStyle}
                    />
                  </Box>          
                </Stack>
              </Box>
            </>
          }
        </>        
      }

      <Stack
        direction='row'
        justifyContent='flex-end'
      >
        <Button
          variant='contained'
          disableRipple
          disableElevation
          disabled={false}
          onClick={handleSubmit}
          sx={saveButtonStyle}
        >
          {defaultTexts.save}
        </Button>
      </Stack>

      <CustomDialog
        open={helperDialogOpen}
        onClose={handleHelperDialogClose}
        dialogBody={{ text: helperText }}
        maxWidth='xs'
      />
    </>
  )
}

export default ServiceAdd